import { msg } from '@lingui/core/macro';
import type {
  CheckConfig,
  CheckResult,
  CheckType,
  IExif,
  Photo,
  PhotoCreate,
  PhotoMetadata,
  PresignedUrl,
  Session,
} from '@transpordiamet/shared';
import * as Sentry from '@sentry/react';
import { GEOLOCATION_CONFIG, IMAGE_QUALITY_CONFIG } from '../config/constants';
import { i18n } from '../i18n';
import api from './api';
import { getErrorDetails } from './errors';
import { isWithinEstonia } from './geoValidator';
import { calculateSharpness } from './imageQualityWorker';
import {
  captureCaptureFailure,
  photoLog,
  readQualityImageData,
  roundStat,
  summarizeImageData,
  type PhotoLogAttributes,
} from './photoTelemetry';

class PhotoQualityError extends Error {
  constructor(
    message: string,
    public details: PhotoLogAttributes
  ) {
    super(message);
    this.name = 'PhotoQualityError';
  }
}

/** A photo the user can fix by retaking it. Reported below error level. */
class PhotoCheckError extends Error {
  constructor(
    message: string,
    public check: 'gps' | 'dimensions'
  ) {
    super(message);
    this.name = 'PhotoCheckError';
  }
}

// Generate a unique filename for the photo
function generatePhotoFilename(): string {
  const timestamp = Date.now();
  const random = Math.random().toString(36).substring(2, 9);
  return `photo_${timestamp}_${random}.jpg`;
}

// Generate R2 path for the photo
function generateR2Path(sessionId: string, stepId: string): string {
  const filename = generatePhotoFilename();
  return `sessions/${sessionId}/${stepId}/${filename}`;
}

/**
 * Perform validation checks on the photo
 * @param photoBlob - The photo to validate
 * @param metadata - Photo metadata (GPS coordinates, timestamp)
 * @param checksToRun - Array of check types to perform
 * @returns Array of check results
 * @throws Error if critical checks fail
 */
async function performChecks(
  photoBlob: Blob,
  metadata: PhotoMetadata,
  checksToRun: CheckType[]
): Promise<CheckResult[]> {
  const results: CheckResult[] = [];

  // Decode image once if needed for dimensions or quality checks
  let img: ImageBitmap | null = null;

  try {
    if (checksToRun.includes('dimensions') || checksToRun.includes('quality')) {
      img = await createImageBitmap(photoBlob);
    }

    for (const checkType of checksToRun) {
      try {
        switch (checkType) {
          case 'gps': {
            if (!GEOLOCATION_CONFIG.ENABLED) break;
            // Check if GPS coordinates are within Estonia
            const { latitude, longitude } = metadata;

            if (!latitude || !longitude) {
              throw new PhotoCheckError(
                i18n._(msg`Photo does not contain GPS data. Please ensure device location service is enabled.`),
                'gps'
              );
            }

            const withinEstonia = await isWithinEstonia(latitude, longitude);

            if (!withinEstonia) {
              throw new PhotoCheckError(i18n._(msg`Photo must be taken within Estonia.`), 'gps');
            }

            results.push({
              type: 'gps',
              value: JSON.stringify({ latitude, longitude }),
              passed: true,
            });

            break;
          }

          case 'dimensions': {
            // Check image dimensions (resolution)
            const width = img!.width;
            const height = img!.height;
            const megapixels = (width * height) / 1_000_000;

            const minWidth = IMAGE_QUALITY_CONFIG.MIN_WIDTH;
            const minHeight = IMAGE_QUALITY_CONFIG.MIN_HEIGHT;
            const maxWidth = IMAGE_QUALITY_CONFIG.MAX_WIDTH;
            const maxHeight = IMAGE_QUALITY_CONFIG.MAX_HEIGHT;

            // Check minimum resolution
            if (width < minWidth || height < minHeight) {
              throw new PhotoCheckError(
                i18n._(
                  msg`Photo resolution is too low (${width}x${height}). Minimum required resolution is ${minWidth}x${minHeight}.`
                ),
                'dimensions'
              );
            }

            // Check maximum resolution
            if (width > maxWidth || height > maxHeight) {
              throw new PhotoCheckError(
                i18n._(
                  msg`Photo resolution is too high (${width}x${height}). Maximum allowed resolution is ${maxWidth}x${maxHeight}.`
                ),
                'dimensions'
              );
            }
            results.push({ type: 'dimensions', value: JSON.stringify({ width, height, megapixels }), passed: true });

            break;
          }

          case 'quality': {
            // Check image quality (sharpness) using Laplacian variance
            // This now uses a web worker to avoid blocking the main thread (~280ms)
            const size = Math.min(img!.width, img!.height, 500);
            const minSharpness = IMAGE_QUALITY_CONFIG.MIN_SHARPNESS;
            photoLog('info', 'Quality check start', {
              blobSize: photoBlob.size,
              blobType: photoBlob.type,
              bitmapWidth: img!.width,
              bitmapHeight: img!.height,
              sampleSize: size,
              minSharpness,
            });

            let imageData: ImageData;
            let canvasBackend: 'offscreen' | 'html';
            try {
              const sampleCanvas = readQualityImageData(img!, size);
              imageData = sampleCanvas.imageData;
              canvasBackend = sampleCanvas.backend;
            } catch (error) {
              const details = {
                sampleSize: size,
                canvasError: error instanceof Error ? error.message : String(error),
              };
              photoLog('warning', 'Quality check failed', details);
              throw new PhotoQualityError(i18n._(msg`Unable to process image for quality check.`), details);
            }

            const sample = summarizeImageData(imageData);
            photoLog('info', 'Quality canvas sample', { ...sample, canvasBackend });

            const sharpnessResult = await calculateSharpness(imageData);
            const sharpness = roundStat(sharpnessResult.sharpness);
            const passesQuality = sharpnessResult.sharpness >= minSharpness;
            const details = {
              sharpness,
              minSharpness,
              passed: passesQuality,
              source: sharpnessResult.source,
              workerError: sharpnessResult.workerError ?? '',
              durationMs: sharpnessResult.durationMs,
              canvasBackend,
              ...sample,
            };

            photoLog(
              passesQuality ? 'info' : 'warning',
              passesQuality ? 'Quality check passed' : 'Quality check failed',
              details
            );

            if (!passesQuality) {
              throw new PhotoQualityError(i18n._(msg`Photo is too blurry. Please take a clearer picture.`), details);
            }

            results.push({ type: 'quality', value: sharpness, passed: true });
            break;
          }

          default:
            console.warn(`Unknown check type: ${checkType}`);
        }
      } catch (error) {
        // Re-throw the error to be handled by the caller
        throw error;
      }
    }

    return results;
  } finally {
    // Always release ImageBitmap resources to prevent memory leaks
    if (img) {
      img.close();
    }
  }
}

export interface UploadPhotoParams {
  sessionId: string;
  stepId: string;
  photoBlob: Blob;
  checksToRun: CheckConfig[];
  exifData: IExif | null;
  metadata: PhotoMetadata;
  /**
   * Called once the photo is in R2 and its public URL has loaded, before the
   * server checks run. publicUrl is unset without R2_PUBLIC_DOMAIN.
   */
  onUploaded?: (publicUrl: string | undefined) => void;
}

export interface UploadPhotoResult {
  success: boolean;
  session?: Session;
  error?: string;
  errorCode?: string; // Backend error code (e.g., 'PHOTO_UPLOAD_TIMEOUT')
  requiresCheckConfirmation?: boolean; // True if awaiting check confirmation (VIN, car detection, etc.)
  checkResult?: CheckResult; // The check result awaiting confirmation
  draftPhoto?: Photo; // Draft photo awaiting confirmation
}

const PUT_ATTEMPTS = 3;
const PUT_RETRY_MS = [400, 1200];

// Browser messages only. They decide whether to retry. Callers never see them.
function isDroppedConnection(error: unknown): boolean {
  // Sentry rewrites Chromium's message to "Failed to fetch (<host>)" before we catch it.
  if (!(error instanceof TypeError)) return false;
  return (
    error.message.startsWith('Failed to fetch') ||
    error.message.startsWith('Load failed') ||
    error.message.startsWith('NetworkError when attempting to fetch resource.')
  );
}

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function photoPutFailed(cause: unknown): Error {
  // ES2020 lib has no ErrorOptions. Sentry's linkedErrors still reads this property.
  const error = new Error(i18n._(msg`Photo upload failed`));
  Object.defineProperty(error, 'cause', { value: cause });
  return error;
}

/**
 * PUT the photo to the presigned URL.
 * A dropped connection or a gateway response is retried against the same URL.
 * The URL stays valid for minutes, and a repeat PUT overwrites the same object.
 */
async function putPhoto(uploadURL: string, body: Blob): Promise<void> {
  let lastCause: unknown;

  for (let attempt = 1; attempt <= PUT_ATTEMPTS; attempt++) {
    let retry = false;

    try {
      const response = await fetch(uploadURL, { method: 'PUT', body });
      if (response.ok) return;
      lastCause = new Error(`R2 PUT responded ${response.status}`);
      retry = response.status === 502 || response.status === 503 || response.status === 504;
    } catch (error) {
      lastCause = error;
      retry = isDroppedConnection(error);
    }

    if (!retry || attempt === PUT_ATTEMPTS) break;
    await wait(PUT_RETRY_MS[attempt - 1]);
  }

  throw photoPutFailed(lastCause);
}

const R2_IMAGE_LOAD_TIMEOUT_MS = 15_000;

function r2PhotoLoadFailed(cause: Error): Error {
  const error = new Error(i18n._(msg`The uploaded photo could not be loaded. Please take the photo again.`));
  error.name = 'R2PhotoLoadError';
  Object.defineProperty(error, 'cause', { value: cause });
  return error;
}

/**
 * Load the stored photo back from R2's public URL. The step shows this image
 * while it validates, so a photo that does not load stops the upload.
 */
function loadR2Image(publicUrl: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    const timer = setTimeout(() => {
      image.src = '';
      reject(r2PhotoLoadFailed(new Error('R2 image load timed out')));
    }, R2_IMAGE_LOAD_TIMEOUT_MS);
    image.onload = () => {
      clearTimeout(timer);
      resolve();
    };
    image.onerror = () => {
      clearTimeout(timer);
      reject(r2PhotoLoadFailed(new Error('R2 image failed to load')));
    };
    image.src = publicUrl;
  });
}

/**
 * Upload a photo for a session step.
 *
 * Workflow:
 * 1. Build EXIF data from injected metadata
 * 2. Perform validation checks (client-side: gps, dimensions, quality)
 * 3. Generate R2 path for the photo
 * 4. Request presigned URL from backend
 * 5. Upload photo to R2 using presigned URL (retried if the connection drops)
 * 6. Load it back from R2's public URL (a failed load stops here), then call onUploaded
 * 7. Create draft photo in database (AI checks run automatically on backend):
 *    - If VIN check passes: return draft photo for user confirmation
 *    - If AI check fails with allow_bypass: return draft photo for bypass confirmation
 *    - Otherwise: auto-confirm the photo
 * 8. Return updated session if photo was auto-confirmed
 */
export async function uploadPhoto({
  sessionId,
  stepId,
  photoBlob,
  checksToRun,
  exifData,
  metadata,
  onUploaded,
}: UploadPhotoParams): Promise<UploadPhotoResult> {
  try {
    // Extract check types for client-side validation
    const checkTypes = checksToRun.map((c) => c.type);
    const checks = await performChecks(photoBlob, metadata, checkTypes);

    const r2Path = generateR2Path(sessionId, stepId);
    const presignResponse = await api.post<PresignedUrl>(`/sessions/${sessionId}/upload/presign`, { r2Path });

    const { uploadURL, key, publicUrl } = presignResponse.data;
    await putPhoto(uploadURL, photoBlob);
    if (publicUrl) await loadR2Image(publicUrl);
    onUploaded?.(publicUrl);

    const photoData: PhotoCreate = {
      stepId,
      r2Path: key,
      exifData: { ...exifData, metadata } as IExif,
      checks,
    };

    // Create draft photo (AI checks run automatically on backend if configured)
    const createResponse = await api.post<Photo>(`/sessions/${sessionId}/upload/create`, photoData);
    const draftPhoto = createResponse.data;

    // Check if any AI checks failed (VIN extraction, car detection)
    const failedAiChecks = draftPhoto.checks.filter(
      (check) => !check.passed && (check.type === 'vin' || check.type === 'is_car')
    );

    if (failedAiChecks.length > 0) {
      // Some AI checks failed - check if they can be bypassed
      const bypassableFailures = failedAiChecks.filter((check) => check.allow_bypass);

      if (bypassableFailures.length > 0) {
        const failure = bypassableFailures[0];
        if (failure.type === 'vin' && (failure.error === 'VIN_MISMATCH' || failure.error === 'VIN_EXTRACTION_FAILED')) {
          captureCaptureFailure('vin check needs confirmation', {
            error: failure.error ?? '',
            allow_bypass: true,
            stepId,
          });
        }
        // There are failed checks that can be bypassed - return for user confirmation
        return {
          success: true,
          requiresCheckConfirmation: true,
          checkResult: bypassableFailures[0], // Return first failed check for display
          draftPhoto,
        };
      }
      // If we get here, some checks failed and cannot be bypassed
      // The API would have returned a 400 error, but just in case
      throw new Error(i18n._(msg`Photo validation failed. Please try again.`));
    }

    // All AI checks passed - check if VIN confirmation is needed
    const vinCheckResult = draftPhoto.checks.find((check) => check.type === 'vin' && check.passed);
    if (vinCheckResult) {
      // VIN extracted successfully - return for user confirmation
      return {
        success: true,
        requiresCheckConfirmation: true,
        checkResult: vinCheckResult,
        draftPhoto,
      };
    }

    // No VIN confirmation needed, auto-confirm the photo
    await api.post<Photo>(`/sessions/${sessionId}/upload/${draftPhoto.id}/confirm`, {});

    // Photo confirmed successfully, now fetch updated session
    const sessionResponse = await api.get<Session>(`/sessions/${sessionId}`);

    return {
      success: true,
      session: sessionResponse.data,
    };
  } catch (error) {
    console.error('[Upload] Failed:', error);

    // This catch turns every failure into a result object, so nothing upstream
    // reports it — a client-side check rejecting the photo included.
    // GPS and resolution rejections are expected; keep them off the error-level
    // alert. Boundary load failures and upload failures stay at error level.
    const quality = error instanceof PhotoQualityError ? error.details : undefined;
    const expectedCheck = error instanceof PhotoCheckError ? error.check : undefined;
    Sentry.captureException(error, {
      ...(expectedCheck ? { level: 'info' as const } : {}),
      tags: {
        stage: 'photo-upload',
        ...(quality ? { 'photo.check': 'quality' } : {}),
        ...(expectedCheck ? { 'photo.check': expectedCheck } : {}),
      },
      contexts: {
        photo: { sessionId, stepId },
        ...(quality ? { photoCheck: quality } : {}),
      },
      fingerprint: quality ? ['photo-quality-check'] : expectedCheck ? ['photo-check', expectedCheck] : undefined,
    });

    const errorDetails = getErrorDetails(error);
    return {
      success: false,
      error: errorDetails.message,
      errorCode: errorDetails.code,
    };
  }
}

/**
 * Complete photo upload after check confirmation.
 * Called after user confirms or bypasses a check result (VIN confirmation, car detection bypass, etc.)
 * Confirms the draft photo that was already created.
 */
export async function completeUploadAfterCheckConfirmation(
  sessionId: string,
  photoId: string
): Promise<UploadPhotoResult> {
  try {
    // Confirm the draft photo (empty body triggers Content-Type: application/json)
    await api.post<Photo>(`/sessions/${sessionId}/upload/${photoId}/confirm`, {});

    // Photo confirmed successfully, now fetch updated session
    const sessionResponse = await api.get<Session>(`/sessions/${sessionId}`);

    return {
      success: true,
      session: sessionResponse.data,
    };
  } catch (error) {
    console.error('[Upload Complete] Failed:', error);

    const errorDetails = getErrorDetails(error);
    return {
      success: false,
      error: errorDetails.message,
      errorCode: errorDetails.code,
    };
  }
}
